import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection, Geometry } from 'geojson';

import { showErrorToast } from '@/errors/showErrorToast';
import { toAppError } from '@/errors/toAppError';

export const EMPTY_FEATURE_COLLECTION: FeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * Attaches an interval data loader to a MapLibre source: fetches immediately, then
 * re-fetches on a fixed cadence for live data (vehicle positions) that changes with time
 * rather than with the viewport. The portal's only recurring poller, so its lifecycle is
 * deliberately strict:
 *   - self-reschedules AFTER each response settles (never overlaps requests on a slow API);
 *   - pauses while the tab is hidden and catches up immediately on return;
 *   - stale responses are dropped via a request-ID counter;
 *   - a 403 stops the poll permanently and calls `onForbidden` instead of toasting — it
 *     means the per-user capability was revoked server-side, not that something broke;
 *   - other failures toast once per failure streak (a 30s poll must not toast every tick
 *     while the API is down), resetting on the next success.
 * Returns a cleanup function suitable for returning from `useEffect`.
 */
export function attachIntervalLoader(
    mapRef: RefObject<MapRef | null>,
    {
        sourceId,
        intervalMs,
        dataRef,
        fetchFn,
        onData,
        onForbidden,
    }: {
        sourceId: string;
        intervalMs: number;
        dataRef: RefObject<FeatureCollection>;
        fetchFn: () => Promise<FeatureCollection>;
        onData?: (map: maplibregl.Map, data: FeatureCollection) => void;
        /** Capability revoked server-side (403): the loader has already stopped itself. */
        onForbidden?: () => void;
    },
): () => void {
    let cancelled = false;
    let requestId = 0;
    let inFlight = false;
    let toastedThisFailureStreak = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
        if (cancelled) return;
        timer = setTimeout(poll, intervalMs);
    };

    const poll = () => {
        if (cancelled) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            // keep the cadence armed but do no work; visibilitychange below catches up
            schedule();
            return;
        }
        if (inFlight) {
            schedule();
            return;
        }

        inFlight = true;
        const thisId = ++requestId;
        fetchFn()
            .then((data) => {
                inFlight = false;
                if (cancelled || requestId !== thisId) return;
                toastedThisFailureStreak = false;
                dataRef.current = data;
                const map = mapRef.current?.getMap();
                if (map) {
                    setGeoJsonSourceData(map, sourceId, data);
                    onData?.(map, data);
                }
                schedule();
            })
            .catch((err: unknown) => {
                inFlight = false;
                if (cancelled) return;
                if (toAppError(err).code === 'forbidden') {
                    cancelled = true;
                    onForbidden?.();
                    return;
                }
                if (!toastedThisFailureStreak) {
                    toastedThisFailureStreak = true;
                    showErrorToast(err);
                }
                schedule();
            });
    };

    const handleVisibilityChange = () => {
        if (cancelled || document.visibilityState !== 'visible') return;
        clearTimeout(timer);
        poll();
    };
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    poll();

    return () => {
        cancelled = true;
        clearTimeout(timer);
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
    };
}

/**
 * Converts a raw GeoJSON coordinates array to a validated [longitude, latitude] tuple.
 * Returns undefined when the input is not a Point or the values are not finite numbers.
 */
export function toLngLatTuple(rawCoords: unknown): [number, number] | undefined {
    if (!Array.isArray(rawCoords)) return undefined;
    const lng: unknown = rawCoords[0];
    const lat: unknown = rawCoords[1];
    if (typeof lng === 'number' && isFinite(lng) && typeof lat === 'number' && isFinite(lat)) {
        return [lng, lat];
    }
    return undefined;
}

/**
 * Returns the centre of a Polygon/MultiPolygon as a [longitude, latitude] tuple — the
 * midpoint of the geometry's bounding box (min/max lng/lat across every ring). Used to
 * centre the map on a selected AOI/DMA feature. Pure math (no MapLibre dependency) so it
 * stays trivially unit-testable. Returns undefined for non-polygon or empty geometry.
 */
export function geometryCenter(geometry: Geometry | null | undefined): [number, number] | undefined {
    if (geometry?.type !== 'Polygon' && geometry?.type !== 'MultiPolygon') return undefined;

    // Polygon coordinates are number[][][]; MultiPolygon are number[][][][]. Normalise to
    // a list of rings so both are handled by the same reducer.
    const rings = geometry.type === 'Polygon' ? geometry.coordinates : geometry.coordinates.flat();

    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const ring of rings) {
        for (const position of ring) {
            const lng = position[0];
            const lat = position[1];
            if (typeof lng !== 'number' || !isFinite(lng) || typeof lat !== 'number' || !isFinite(lat)) continue;
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        }
    }

    if (minLng === Infinity) return undefined; // no finite coordinates found
    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
}

/**
 * Ray-casting point-in-polygon test against a GeoJSON Polygon or MultiPolygon.
 * A Polygon contains the point when it is inside the outer ring and not inside
 * any hole ring; a MultiPolygon when any member polygon contains it. Pure math
 * (no MapLibre dependency) so it stays trivially unit-testable. Returns false
 * for non-polygon/empty geometry or non-finite coordinates. UK-scoped data, so
 * no antimeridian handling.
 */
export function pointInPolygon(point: [number, number], geometry: Geometry | null | undefined): boolean {
    const [lng, lat] = point;
    if (!isFinite(lng) || !isFinite(lat)) return false;
    if (geometry?.type !== 'Polygon' && geometry?.type !== 'MultiPolygon') return false;

    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    return polygons.some((rings) => {
        if (rings.length === 0) return false;
        const [outer, ...holes] = rings;
        if (!pointInRing(lng, lat, outer)) return false;
        return !holes.some((hole) => pointInRing(lng, lat, hole));
    });
}

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i][0];
        const yi = ring[i][1];
        const xj = ring[j][0];
        const yj = ring[j][1];
        if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}

export function formatBbox(bounds: maplibregl.LngLatBounds): string {
    return [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join(',');
}

export function bboxContains(outer: maplibregl.LngLatBounds, inner: maplibregl.LngLatBounds): boolean {
    return (
        outer.getWest() <= inner.getWest() &&
        outer.getSouth() <= inner.getSouth() &&
        outer.getEast() >= inner.getEast() &&
        outer.getNorth() >= inner.getNorth()
    );
}

export function setGeoJsonSourceData(mlMap: maplibregl.Map, sourceId: string, data: FeatureCollection): void {
    const source = mlMap.getSource(sourceId);
    if (source) {
        (source as maplibregl.GeoJSONSource).setData(data);
    }
}

/**
 * Attaches a viewport data loader to a MapLibre map: fetches data on call, then re-fetches
 * on every `moveend` (300ms debounced). Skips fetch and clears source when zoom < minZoom.
 * Guards against stale responses with a request-ID counter and a cancellation flag.
 * Skips the fetch when the current viewport is already contained within a previously-fetched
 * bounding box (bbox containment cache, scoped to the lifetime of this loader attachment).
 * Returns a cleanup function suitable for returning from `useEffect`.
 */
export function attachViewportLoader(
    mapRef: RefObject<MapRef | null>,
    {
        sourceId,
        minZoom,
        clientId,
        dataRef,
        fetchFn,
        transformFn,
        onData,
    }: {
        sourceId: string;
        minZoom: number;
        clientId: string;
        dataRef: RefObject<FeatureCollection>;
        fetchFn: (clientId: string, bbox: string) => Promise<FeatureCollection>;
        transformFn?: (data: FeatureCollection) => FeatureCollection;
        /**
         * Called with the raw loaded data on every path that updates the primary source:
         * fetch-success, cache-hit, and the below-minZoom clear (passed EMPTY_FEATURE_COLLECTION).
         * Lets a second source be fed from the same fetch/cache cycle (e.g. splitting one
         * response across two clustered sources).
         */
        onData?: (map: maplibregl.Map, data: FeatureCollection) => void;
    },
): () => void {
    let cancelled = false;
    let requestId = 0;
    const cache: Array<{ bounds: maplibregl.LngLatBounds; data: FeatureCollection }> = [];

    const loadViewport = () => {
        const map = mapRef.current?.getMap();
        if (!map) return;
        const zoom = Math.floor(map.getZoom());
        if (zoom < minZoom) {
            dataRef.current = EMPTY_FEATURE_COLLECTION;
            setGeoJsonSourceData(map, sourceId, EMPTY_FEATURE_COLLECTION);
            onData?.(map, EMPTY_FEATURE_COLLECTION);
            return;
        }
        const currentBounds = map.getBounds();

        const hit = cache.find((entry) => bboxContains(entry.bounds, currentBounds));
        if (hit) {
            dataRef.current = hit.data;
            setGeoJsonSourceData(map, sourceId, transformFn ? transformFn(hit.data) : hit.data);
            onData?.(map, hit.data);
            return;
        }

        const bbox = formatBbox(currentBounds);
        const thisId = ++requestId;
        fetchFn(clientId, bbox)
            .then((data) => {
                if (cancelled || requestId !== thisId) return;
                cache.push({ bounds: currentBounds, data });
                dataRef.current = data;
                const m = mapRef.current?.getMap();
                if (m) {
                    setGeoJsonSourceData(m, sourceId, transformFn ? transformFn(data) : data);
                    onData?.(m, data);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) showErrorToast(err);
            });
    };

    loadViewport();

    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const handleMoveEnd = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            if (!cancelled) loadViewport();
        }, 300);
    };

    const map = mapRef.current?.getMap();
    if (map) map.on('moveend', handleMoveEnd);

    return () => {
        cancelled = true;
        clearTimeout(debounceTimer);
        if (map) map.off('moveend', handleMoveEnd);
    };
}
